import React from 'react';
import { FiEdit2, FiSave } from 'react-icons/fi';
import { FaTimes, FaCheck, FaChevronDown, FaChevronUp, FaPlus } from 'react-icons/fa';
import EmojiPicker from 'emoji-picker-react';

const EditModal = ({
  showEditModal,
  setShowEditModal,
  editModalTitle,
  setEditModalTitle,
  editModalDescription,
  setEditModalDescription,
  editModalIcon,
  setEditModalIcon,
  editModalStatus,
  setEditModalStatus,
  editModalTags,
  setEditModalTags,
  editModalNewTag,
  setEditModalNewTag,
  showEditEmojiPicker,
  setShowEditEmojiPicker,
  editingTagIndex,
  setEditingTagIndex,
  editingTagValue,
  setEditingTagValue,
  showTagsSection,
  setShowTagsSection,
  handleEditModalSave,
  handleEditModalAddTag,
  handleEditModalDeleteTag,
  handleEditTag,
  handleSaveTagEdit,
  handleCancelTagEdit
}) => {
  if (!showEditModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-[var(--primary)]/10 flex items-center justify-center">
              <FiEdit2 className="text-xl text-[var(--primary)]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Edit Idea</h3>
              <p className="text-sm text-[var(--text-secondary)]">Update your idea details</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Icon and Title */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <button
                  className="text-4xl p-3 rounded-lg hover:bg-[var(--bg-hover)] transition"
                  onClick={() => setShowEditEmojiPicker(!showEditEmojiPicker)}
                >
                  {editModalIcon}
                </button>
                {showEditEmojiPicker && (
                  <div className="absolute z-10 mt-2">
                    <EmojiPicker
                      onEmojiClick={(emojiData) => {
                        setEditModalIcon(emojiData.emoji);
                        setShowEditEmojiPicker(false);
                      }}
                    />
                  </div>
                )}
              </div>
              <input
                type="text"
                className="flex-1 text-xl font-bold p-3 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] placeholder-[var(--text-secondary)]"
                value={editModalTitle}
                onChange={(e) => setEditModalTitle(e.target.value)}
                placeholder="Idea title"
              />
            </div>

            {/* Description and Status Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Description */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">Description</label>
                <textarea
                  className="w-full p-4 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] placeholder-[var(--text-secondary)] text-base leading-relaxed"
                  value={editModalDescription}
                  onChange={(e) => setEditModalDescription(e.target.value)}
                  rows="6"
                  placeholder="Describe your idea in detail..."
                  style={{ fontSize: '16px' }} // Prevent zoom on iOS
                />
              </div>

              {/* Status */}
              <div className="lg:col-span-1">
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">Status</label>
                <select
                  className="w-full p-3 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                  value={editModalStatus}
                  onChange={(e) => setEditModalStatus(e.target.value)}
                >
                  <option value="draft">Draft</option>
                  <option value="in-progress">In Progress</option>
                  <option value="review">Under Review</option>
                  <option value="completed">Completed</option>
                  <option value="on-hold">On Hold</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            {/* Tags - Collapsible Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[var(--text-secondary)]">
                  Tags {editModalTags.length > 0 && (
                    <span className="ml-1 px-2 py-0.5 bg-[var(--primary)]/20 text-[var(--primary)] rounded-full text-xs">
                      {editModalTags.length}
                    </span>
                  )}
                </label>
                <button
                  type="button"
                  onClick={() => setShowTagsSection(!showTagsSection)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-all duration-200 touch-manipulation"
                >
                  {showTagsSection ? (
                    <>
                      <FaChevronUp className="w-3 h-3" />
                      <span>Hide Tags</span>
                    </>
                  ) : (
                    <>
                      <FaChevronDown className="w-3 h-3" />
                      <span>View & Edit Tags</span>
                    </>
                  )}
                </button>
              </div>
              
              {/* Add Tag Input - Always Visible */}
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  className="flex-1 p-3 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] placeholder-[var(--text-secondary)]"
                  value={editModalNewTag}
                  onChange={(e) => setEditModalNewTag(e.target.value)}
                  onKeyDown={handleEditModalAddTag}
                  placeholder="Add a tag (press Enter)"
                  style={{ fontSize: '16px' }} // Prevent zoom on iOS
                />
                <button
                  type="button"
                  onClick={() => {
                    if (editModalNewTag.trim() && !editModalTags.includes(editModalNewTag.trim())) {
                      setEditModalTags([...editModalTags, editModalNewTag.trim()]);
                      setEditModalNewTag('');
                    }
                  }}
                  className="px-4 py-3 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all duration-200 hover:scale-105 text-sm touch-manipulation min-w-[50px]"
                  disabled={!editModalNewTag.trim() || editModalTags.includes(editModalNewTag.trim())}
                >
                  <FaPlus />
                </button>
              </div>
              
              {/* Collapsible Tags Content */}
              <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                showTagsSection ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2 mb-3 p-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] min-h-[40px]">
                    {editModalTags.map((tag, index) => (
                      <div key={index} className="inline-flex">
                        {editingTagIndex === index ? (
                          <div className="flex items-center gap-2 px-3 py-2 bg-[var(--primary)]/10 border border-[var(--primary)]/50 rounded-lg min-w-[120px]">
                            <input
                              type="text"
                              value={editingTagValue}
                              onChange={(e) => setEditingTagValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveTagEdit(index);
                                } else if (e.key === 'Escape') {
                                  handleCancelTagEdit();
                                }
                              }}
                              className="text-sm bg-transparent border-none outline-none text-[var(--text-primary)] flex-1 min-w-0"
                              style={{ fontSize: '16px' }} // Prevent zoom on iOS
                              autoFocus
                            />
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() => handleSaveTagEdit(index)}
                                className="p-1 text-[var(--primary)] hover:text-[var(--primary-dark)] transition-colors touch-manipulation"
                                title="Save"
                              >
                                <FaCheck className="w-4 h-4" />
                              </button>
                              <button
                                onClick={handleCancelTagEdit}
                                className="p-1 text-[var(--text-secondary)] hover:text-[var(--danger)] transition-colors touch-manipulation"
                                title="Cancel"
                              >
                                <FaTimes className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 px-3 py-2 bg-[var(--bg-hover)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-lg text-sm whitespace-nowrap touch-manipulation">
                            <span
                              onClick={() => handleEditTag(index, tag)}
                              className="flex-1 cursor-pointer"
                              title="Tap to edit"
                            >
                              {tag}
                            </span>
                            <button
                              onClick={() => handleEditModalDeleteTag(tag)}
                              className="p-1 text-[var(--danger)] hover:text-[var(--danger-dark)] transition-all duration-200 touch-manipulation ml-1"
                              title="Remove tag"
                            >
                              <FaTimes className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    {editModalTags.length === 0 && (
                      <span className="text-sm text-[var(--text-secondary)] italic">No tags added yet</span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Quick Tag Preview (when collapsed) */}
              {!showTagsSection && editModalTags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {editModalTags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-[var(--bg-hover)] text-[var(--text-primary)] rounded text-xs border border-[var(--border-color)]"
                    >
                      {tag}
                    </span>
                  ))}
                  {editModalTags.length > 3 && (
                    <span className="px-2 py-1 text-[var(--text-secondary)] text-xs">
                      +{editModalTags.length - 3} more
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowEditModal(false)}
              className="px-4 py-2 rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-color)] hover:bg-[var(--bg-hover)] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleEditModalSave}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all duration-200 hover:scale-105"
            >
              <FiSave /> Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditModal;
